/** /tour-operators/:id/lifecycle — a tenant's lifecycle (tenant retirement CONTRACT §8.7): its state,
 *  the open request with its decisions, two-person approval, the release gate, and the recent
 *  lifecycle requests with what each decision did.
 *
 *  Reading needs `console_tenant_lifecycle_view` (the tab is not drawn without it); every change
 *  needs `console_tenant_lifecycle_manage` (Ops Admin) and is audited server-side. A close, a code
 *  release and a reopen are each a request first, then an approval.
 *
 *  While an approved release or restore job runs, the state is re-read every few seconds (its
 *  steps move on); the polling stops when the request is applied or failed, and the operator
 *  record and the lists are re-read then (the banner follows the new lifecycle). Failing reads
 *  back off (the period doubles per failure, up to 30 s) and keep the last state on screen; after
 *  10 minutes the tab stops following on its own and the Job row offers Refresh. */
import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import { lifecycleApi, lifecycleErrorWords, lifecycleKeys } from '@/api/tenantLifecycle';
import { TENANT_REQUEST_KIND_LABELS, type TenantRequest } from '@/api/tenantRequests';
import type { TourOperatorDetail } from '@/api/tourOperators';
import { Section } from '@/features/partners/PartnerSection';
import { TenantRequestStatusChip } from '@/features/tenant-requests/tenantRequestFormat';
import { userLabel } from '@/features/onboarding-invites/inviteFormat';
import { When } from '@/features/tour-operators/sandbox/When';

import { cascadeSummary, lifecycleDecisionText } from './lifecycleFormat';
import {
  isJobRunning,
  jobRefetchInterval,
  LIFECYCLE_POLL_CEILING_MS,
  LIFECYCLE_POLL_MS,
  type PollFailures,
} from './lifecycleJob';
import { LifecycleStateSection } from './LifecycleStateSection';
import { useLifecycleRefresh } from './useLifecycleRefresh';

export function LifecycleTab({
  to,
  pollMs = LIFECYCLE_POLL_MS,
  pollCeilingMs = LIFECYCLE_POLL_CEILING_MS,
}: {
  to: Pick<TourOperatorDetail, 'id'>;
  /** How often a running job's state is re-read (tests shorten it). */
  pollMs?: number;
  /** How long a running job is followed before the tab stops and offers Refresh (tests shorten it). */
  pollCeilingMs?: number;
}) {
  const refresh = useLifecycleRefresh();
  const failures = useRef<PollFailures>({ seen: 0, count: 0 });
  const [stopped, setStopped] = useState(false);
  const query = useQuery({
    queryKey: lifecycleKeys.state(to.id),
    queryFn: () => lifecycleApi.state(to.id),
    refetchInterval: (q) => (stopped ? false : jobRefetchInterval(q.state, failures.current, pollMs)),
  });
  const running = isJobRunning(query.data);
  // A job just ended (applied or failed): what shows the lifecycle elsewhere follows.
  const wasRunning = useRef(running);
  useEffect(() => {
    if (wasRunning.current && !running) refresh();
    wasRunning.current = running;
  }, [running, refresh]);
  // Followed this long, the tab stops on its own; no job running, the next one is followed afresh.
  useEffect(() => {
    if (!running) {
      setStopped(false);
      return;
    }
    if (stopped) return;
    const timer = setTimeout(() => setStopped(true), pollCeilingMs);
    return () => clearTimeout(timer);
  }, [running, stopped, pollCeilingMs]);
  const { refetch } = query;
  const resume = useCallback(() => {
    failures.current.count = 0;
    setStopped(false);
    void refetch({ cancelRefetch: false });
  }, [refetch]);
  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  // A failed re-read keeps the last state on screen (the Job row says the reads fail).
  if (!query.data) {
    return (
      <Alert severity="error">
        {query.error instanceof ConsoleApiError && query.error.status === 403
          ? 'Reading the lifecycle needs the View tenant lifecycle permission.'
          : lifecycleErrorWords(query.error, 'Couldn’t load the tenant’s lifecycle.')}
      </Alert>
    );
  }
  const s = query.data;
  return (
    <Stack spacing={2} sx={{ maxWidth: 1100, pb: 4 }}>
      <Section title="State">
        <LifecycleStateSection
          state={s}
          follow={{ failing: query.isRefetchError, stopped: running && stopped, onResume: resume }}
        />
      </Section>
      <Section title="Requests">
        <RecentRequests requests={s.recent_requests} />
      </Section>
    </Stack>
  );
}

function RecentRequests({ requests }: { requests: TenantRequest[] }) {
  if (requests.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No lifecycle request yet.
      </Typography>
    );
  }
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label="Lifecycle requests">
        <TableHead>
          <TableRow>
            <TableCell>Requested</TableCell>
            <TableCell>Kind</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>By</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell>Decision</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {requests.map((r) => {
            const cascades = cascadeSummary(r);
            const decision = lifecycleDecisionText(r);
            return (
              <TableRow key={r.id} data-request-id={r.id} sx={{ verticalAlign: 'top' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <When iso={r.requested_ts} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{TENANT_REQUEST_KIND_LABELS[r.kind]}</TableCell>
                <TableCell>
                  <TenantRequestStatusChip status={r.status} />
                </TableCell>
                <TableCell sx={{ overflowWrap: 'anywhere' }}>{userLabel(r.requested_by)}</TableCell>
                <TableCell sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', minWidth: 200 }}>
                  {r.lifecycle?.reason || '—'}
                  {r.lifecycle?.override_reason && (
                    <Box sx={{ color: 'text.secondary' }}>
                      Waiting period overridden: {r.lifecycle.override_reason}
                    </Box>
                  )}
                </TableCell>
                <TableCell sx={{ overflowWrap: 'anywhere', minWidth: 200 }}>
                  {decision || '—'}
                  {r.decided_ts && (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {' · '}
                      <When iso={r.decided_ts} />
                    </Box>
                  )}
                  {cascades && <Box sx={{ color: 'text.secondary', mt: 0.25 }}>{cascades}</Box>}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
